import React, { useState, useEffect, useCallback } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Modal from 'react-bootstrap/Modal';
import Nav from 'react-bootstrap/Nav';
import ForestMonitoring from './ForestMonitoring';
import { API_BASE, fetchCsrfToken, TablePagination } from './adminShared';
import './Admin.css';

function StatusBadge({ status }) {
  const variants = {
    submitted: 'primary',
    under_review: 'warning',
    approved: 'success',
    denied: 'danger',
    expired: 'secondary',
    pending: 'warning',
  };
  const labels = {
    submitted: 'Submitted',
    under_review: 'Under Review',
    approved: 'Approved',
    denied: 'Denied',
    expired: 'Expired',
    pending: 'Pending',
  };
  return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
}

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('messages');

  // Data
  const [messages, setMessages] = useState([]);
  const [roeEntries, setRoeEntries] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Pagination — page number and metadata kept per tab so switching tabs
  // restores where the user was.
  const [page, setPage] = useState({ messages: 1, roe: 1 });
  const [pageInfo, setPageInfo] = useState({ messages: null, roe: null });

  // Detail modal
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailType, setDetailType] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const fetchData = useCallback(async (tab, pageNum) => {
    setDataLoading(true);
    try {
      const endpoint = tab === 'messages' ? 'messages' : 'right-of-entry';
      const response = await fetch(`${API_BASE}/admin/${endpoint}/?page=${pageNum}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const results = data.results || data;
        if (tab === 'messages') {
          setMessages(results);
        } else {
          setRoeEntries(results);
        }
        setPageInfo((prev) => ({
          ...prev,
          [tab]: typeof data.count === 'number'
            ? { count: data.count, hasNext: !!data.next, hasPrev: !!data.previous }
            : null,
        }));
      }
    } catch {
      // Fetch failed silently
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData(activeTab, page[activeTab]);
    }
  }, [isAuthenticated, activeTab, page, fetchData]);

  const goToPage = (tab, pageNum) => {
    setPage((prev) => ({ ...prev, [tab]: pageNum }));
  };

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/check-auth/`, {
        credentials: 'include',
      });
      const data = await response.json();
      setIsAuthenticated(data.authenticated);
      if (data.authenticated) {
        fetchStats();
      }
    } catch {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/dashboard/stats/`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch {
      // Stats fetch failed silently
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const csrfToken = await fetchCsrfToken();

      const response = await fetch(`${API_BASE}/admin/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsAuthenticated(true);
        fetchStats();
      } else {
        setError(data.error || 'Login failed');
      }
    } catch {
      setError('Connection error. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      const csrfToken = await fetchCsrfToken();
      await fetch(`${API_BASE}/admin/logout/`, {
        method: 'POST',
        headers: { 'X-CSRFToken': csrfToken },
        credentials: 'include',
      });
    } catch {
      // Logout failed silently
    }
    setIsAuthenticated(false);
    setStats(null);
    setMessages([]);
    setRoeEntries([]);
    setPage({ messages: 1, roe: 1 });
    setPageInfo({ messages: null, roe: null });
  };

  const handleUpdateStatus = async (type, id, newStatus) => {
    try {
      const csrfToken = await fetchCsrfToken();
      const endpoint = type === 'message'
        ? `${API_BASE}/admin/messages/${id}/`
        : `${API_BASE}/admin/right-of-entry/${id}/`;
      const body = type === 'message'
        ? { is_read: true }
        : { status: newStatus };

      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (response.ok) {
        fetchData(activeTab, page[activeTab]);
        fetchStats();
        if (selectedItem && selectedItem.id === id) {
          const updated = await response.json();
          setSelectedItem(updated);
        }
      }
    } catch {
      // Update failed silently
    }
  };

  const openDetail = (item, type) => {
    setSelectedItem(item);
    setDetailType(type);
    setShowDetail(true);
  };

  const closeDetail = () => {
    setShowDetail(false);
    setSelectedItem(null);
    setDetailType(null);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Container className="py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-4">
            <Card className="admin-login-card shadow-sm">
              <Card.Body className="p-4">
                <h3 className="text-center mb-4">Admin Login</h3>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form onSubmit={handleLogin}>
                  <Form.Group className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <div className="d-grid">
                    <Button variant="primary" type="submit">Login</Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <>
      {/* Detail Modal */}
      <Modal show={showDetail} onHide={closeDetail} size="lg" centered scrollable>
        <Modal.Header closeButton className="admin-modal-header">
          <Modal.Title className="fs-6">
            {detailType === 'message' ? 'Contact Message' : 'Right of Entry'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="admin-modal-body">
          {selectedItem && detailType === 'message' && (
            <div className="admin-detail">
              <div className="admin-detail-section">
                <h6 className="admin-detail-label">Contact Information</h6>
                <Row className="g-3">
                  <Col sm={6}><div className="admin-field"><span>Name</span><p>{selectedItem.first_name} {selectedItem.last_name}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Email</span><p>{selectedItem.email}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Phone</span><p>{selectedItem.phone || 'N/A'}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Organization</span><p>{selectedItem.organization || 'N/A'}</p></div></Col>
                </Row>
              </div>
              <div className="admin-detail-section">
                <h6 className="admin-detail-label">Message Details</h6>
                <Row className="g-3">
                  <Col sm={6}><div className="admin-field"><span>Inquiry Type</span><p>{selectedItem.inquiry_type}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Date</span><p>{new Date(selectedItem.created_at).toLocaleDateString()}</p></div></Col>
                  <Col xs={12}><div className="admin-field"><span>Subject</span><p>{selectedItem.subject}</p></div></Col>
                  <Col xs={12}><div className="admin-field"><span>Message</span><p className="admin-message-text">{selectedItem.message}</p></div></Col>
                </Row>
              </div>
              <div className="admin-detail-actions">
                {!selectedItem.is_read && (
                  <Button variant="primary" size="sm" onClick={() => handleUpdateStatus('message', selectedItem.id)}>
                    Mark as Read
                  </Button>
                )}
                {selectedItem.is_read && <Badge bg="success">Read</Badge>}
              </div>
            </div>
          )}

          {selectedItem && detailType === 'roe' && (
            <div className="admin-detail">
              <div className="admin-detail-section">
                <h6 className="admin-detail-label">Property Information</h6>
                <Row className="g-3">
                  <Col xs={12}><div className="admin-field"><span>Address</span><p>{selectedItem.property_address}</p></div></Col>
                  <Col sm={4}><div className="admin-field"><span>City</span><p>{selectedItem.property_city}</p></div></Col>
                  <Col sm={4}><div className="admin-field"><span>State</span><p>{selectedItem.property_state}</p></div></Col>
                  <Col sm={4}><div className="admin-field"><span>Zip Code</span><p>{selectedItem.property_zip_code}</p></div></Col>
                  <Col sm={4}><div className="admin-field"><span>County</span><p>{selectedItem.property_county || 'N/A'}</p></div></Col>
                  <Col sm={4}><div className="admin-field"><span>Parcel #</span><p>{selectedItem.parcel_number || 'N/A'}</p></div></Col>
                  <Col sm={4}><div className="admin-field"><span>Acreage</span><p>{selectedItem.property_acreage || 'N/A'}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Property Type</span><p>{selectedItem.property_type}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>HOA</span><p>{selectedItem.is_hoa ? (selectedItem.hoa_name || 'Yes (name not provided)') : 'No'}</p></div></Col>
                </Row>
              </div>
              <div className="admin-detail-section">
                <h6 className="admin-detail-label">Owner / Grantor</h6>
                <Row className="g-3">
                  <Col sm={6}><div className="admin-field"><span>Name</span><p>{selectedItem.owner_first_name} {selectedItem.owner_last_name}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Email</span><p>{selectedItem.owner_email}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Phone</span><p>{selectedItem.owner_phone}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Mailing Address</span><p>{selectedItem.owner_mailing_address || 'Same as property'}</p></div></Col>
                </Row>
              </div>
              <div className="admin-detail-section">
                <h6 className="admin-detail-label">Scope &amp; Access</h6>
                <Row className="g-3">
                  <Col sm={6}><div className="admin-field"><span>Scope Accepted</span><p>{selectedItem.scope_accepted ? 'Yes' : 'No'}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Preferred Contact</span><p>{selectedItem.preferred_contact_method}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Contact Time</span><p>{selectedItem.preferred_contact_time || 'N/A'}</p></div></Col>
                  <Col xs={12}><div className="admin-field"><span>Access Instructions</span><p className="admin-message-text">{selectedItem.access_instructions || 'None provided'}</p></div></Col>
                </Row>
              </div>
              <div className="admin-detail-section">
                <h6 className="admin-detail-label">Certification</h6>
                <Row className="g-3">
                  <Col sm={6}><div className="admin-field"><span>Certified Authorized</span><p>{selectedItem.certify_authorized ? 'Yes' : 'No'}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Terms Accepted</span><p>{selectedItem.terms_accepted ? 'Yes' : 'No'}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Electronic Signature</span><p className="admin-signature">{selectedItem.electronic_signature}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Signature Date</span><p>{selectedItem.signature_date}</p></div></Col>
                  <Col sm={6}><div className="admin-field"><span>Submitted</span><p>{new Date(selectedItem.created_at).toLocaleDateString()}</p></div></Col>
                  <Col sm={6}>
                    <div className="admin-field">
                      <span>ROE Expires</span>
                      <p>{selectedItem.signature_date
                        ? new Date(new Date(selectedItem.signature_date).getTime() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString()
                        : 'N/A'}
                      </p>
                    </div>
                  </Col>
                </Row>
              </div>
              <div className="admin-detail-actions">
                <span className="me-2">Status:</span>
                <StatusBadge status={selectedItem.status} />
                <div className="admin-status-buttons mt-2">
                  {['submitted', 'under_review', 'approved', 'denied', 'expired'].map((s) => (
                    <Button
                      key={s}
                      variant={selectedItem.status === s ? 'primary' : 'outline-secondary'}
                      size="sm"
                      disabled={selectedItem.status === s}
                      onClick={() => handleUpdateStatus('roe', selectedItem.id, s)}
                    >
                      {s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Container className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Admin Dashboard</h2>
          <Button variant="outline-danger" size="sm" onClick={handleLogout}>Logout</Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <Row className="g-3 mb-4">
            <Col md={4}>
              <Card className="admin-stat-card shadow-sm">
                <Card.Body>
                  <h6 className="text-muted mb-2">Contact Messages</h6>
                  <div className="d-flex align-items-baseline gap-3">
                    <span className="admin-stat-number">{stats.messages.total}</span>
                    {stats.messages.unread > 0 && (
                      <Badge bg="warning" text="dark">{stats.messages.unread} unread</Badge>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="admin-stat-card shadow-sm">
                <Card.Body>
                  <h6 className="text-muted mb-2">Right of Entry</h6>
                  <div className="d-flex align-items-baseline gap-3">
                    <span className="admin-stat-number">{stats.right_of_entry.total}</span>
                    {stats.right_of_entry.submitted > 0 && (
                      <Badge bg="primary">{stats.right_of_entry.submitted} new</Badge>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="admin-stat-card shadow-sm">
                <Card.Body>
                  <h6 className="text-muted mb-2">Volunteers</h6>
                  <div className="d-flex align-items-baseline gap-3">
                    <span className="admin-stat-number">{stats.volunteers.total}</span>
                    {stats.volunteers.pending > 0 && (
                      <Badge bg="warning" text="dark">{stats.volunteers.pending} pending</Badge>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Top-level section tabs */}
        <div className="admin-tab-header admin-top-tabs">
          <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
            <Nav.Item>
              <Nav.Link eventKey="messages">
                Contact Messages
                {stats && stats.messages.unread > 0 && (
                  <Badge bg="warning" text="dark" className="ms-2">{stats.messages.unread}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="roe">
                Right of Entry
                {stats && stats.right_of_entry.submitted > 0 && (
                  <Badge bg="primary" className="ms-2">{stats.right_of_entry.submitted}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="monitoring">Forest Monitoring</Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        {activeTab === 'monitoring' ? (
          <ForestMonitoring />
        ) : (
        <Card className="admin-data-card shadow-sm admin-tab-panel">
          <Card.Body className="p-0">
            {dataLoading ? (
              <div className="text-center py-5 text-muted">Loading records...</div>
            ) : activeTab === 'messages' ? (
              messages.length === 0 ? (
                <div className="text-center py-5 text-muted">No contact messages yet.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table admin-table mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Subject</th>
                        <th>Type</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.map((msg) => (
                        <tr
                          key={msg.id}
                          className={`admin-row ${!msg.is_read ? 'admin-row-unread' : ''}`}
                          onClick={() => openDetail(msg, 'message')}
                        >
                          <td>{new Date(msg.created_at).toLocaleDateString()}</td>
                          <td className="fw-medium">{msg.first_name} {msg.last_name}</td>
                          <td>{msg.email}</td>
                          <td className="admin-cell-truncate">{msg.subject}</td>
                          <td><span className="admin-type-tag">{msg.inquiry_type}</span></td>
                          <td>{msg.is_read ? <Badge bg="success">Read</Badge> : <Badge bg="warning" text="dark">Unread</Badge>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <TablePagination
                    info={pageInfo.messages}
                    page={page.messages}
                    onPageChange={(n) => goToPage('messages', n)}
                  />
                </div>
              )
            ) : (
              roeEntries.length === 0 ? (
                <div className="text-center py-5 text-muted">No right of entry submissions yet.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table admin-table mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Owner</th>
                        <th>Property</th>
                        <th>City, State</th>
                        <th>Expires</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roeEntries.map((entry) => {
                        const expires = entry.signature_date
                          ? new Date(new Date(entry.signature_date).getTime() + 90 * 24 * 60 * 60 * 1000)
                          : null;
                        const isExpired = expires && expires < new Date();
                        return (
                          <tr
                            key={entry.id}
                            className={`admin-row ${entry.status === 'submitted' ? 'admin-row-unread' : ''}`}
                            onClick={() => openDetail(entry, 'roe')}
                          >
                            <td>{new Date(entry.created_at).toLocaleDateString()}</td>
                            <td className="fw-medium">{entry.owner_first_name} {entry.owner_last_name}</td>
                            <td className="admin-cell-truncate">{entry.property_address}</td>
                            <td>{entry.property_city}, {entry.property_state}</td>
                            <td className={isExpired ? 'text-danger' : ''}>
                              {expires ? expires.toLocaleDateString() : 'N/A'}
                            </td>
                            <td><StatusBadge status={entry.status} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <TablePagination
                    info={pageInfo.roe}
                    page={page.roe}
                    onPageChange={(n) => goToPage('roe', n)}
                  />
                </div>
              )
            )}
          </Card.Body>
        </Card>
        )}
      </Container>
    </>
  );
}

export default Admin;
